"use client";

import React, { useMemo } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { SimpleFieldConfig } from "../../UniversalCRMTable/types";
import type { BaseTemplate } from "../../UniversalEntityPage/types";
import type { FieldRenderContext } from "../form-field-renderers/context";
import { renderField } from "../form-field-renderers/dispatcher";
import { FormFieldEntityProvider } from "../form-field-renderers/context";
import { FormActions } from "../FormActions";
import { FormSectionGroup } from "@/components/shared/forms/FormSectionGroup";
import {
  FORM_SECTION_TAXONOMY,
  type FormSectionTaxonomyId,
} from "@/components/shared/forms/FormSectionGroup/section-taxonomy";
import type { FormSectionDescriptor } from "@/components/shared/forms/FormSectionGroup/types";
import type { EntityFormSectionConfig } from "@/lib/types/forms/entity-form-sections";
import { resolveSectionKind } from "./section-kind";

interface FormContentProps {
  /** Used for form id so only one form per entity is clearly identified (avoids duplicate submit from hidden forms) */
  entityName?: string;
  editableFields: Array<[string, SimpleFieldConfig]>;
  createFieldContext: (fieldName: string) => FieldRenderContext;
  TemplateSelector: React.ComponentType<any> | undefined;
  handleTemplateSelect: (template: BaseTemplate | null) => void;
  selectedTemplate: BaseTemplate | null;
  isCreating: boolean;
  isLoading: boolean;
  isSubmitting: boolean;
  hasValidationErrors: boolean;
  submissionError: string | null;
  displayName: string;
  onSubmit: () => Promise<void>;
  onSaveTemplate?: () => void;
  onCancel?: () => void;
  onFormSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  /** Progressive-disclosure section layout. Falls back to flat grid when undefined. */
  sections?: readonly EntityFormSectionConfig[];
  /** React Hook Form instance — required when `sections` is provided. */
  form?: UseFormReturn<Record<string, unknown>>;
}

/**
 * Render a single field (matching by name) using the shared field
 * dispatcher + context factory.
 */
function FieldByName({
  fieldName,
  field,
  createFieldContext,
}: {
  fieldName: string;
  field: SimpleFieldConfig;
  createFieldContext: (fieldName: string) => FieldRenderContext;
}) {
  const ctx = createFieldContext(fieldName);
  const formSpan = field.formSpan ?? 3;
  // min-w-0: grid items default to min-width:auto, so a field whose rendered
  // content has a wide min-content (a select's placeholder/label, a long
  // option) would force this column wider than its track — squeezing or
  // overlapping the sibling column instead of just filling its own row. Same
  // fix as ComposeMainFields' "From Account" column.
  const spanClasses: Record<number, string> = {
    1: "col-span-1 min-w-0",
    2: "col-span-1 min-w-0 md:col-span-2 lg:col-span-2",
    3: "col-span-1 min-w-0 md:col-span-2 lg:col-span-2",
  };
  const colSpanClass = spanClasses[formSpan] ?? spanClasses[3];
  return <div className={colSpanClass}>{renderField(ctx)}</div>;
}

/**
 * Map an EntityFormSectionConfig (data-only) to a FormSectionDescriptor
 * (component-aware). Renders the section's fields via the standard 2-col
 * grid + `renderField` dispatcher, identical to the flat-grid fallback.
 */
function toDescriptor(
  section: EntityFormSectionConfig,
  fieldsByName: Map<string, SimpleFieldConfig>,
  createFieldContext: (fieldName: string) => FieldRenderContext
): FormSectionDescriptor<Record<string, unknown>> {
  const taxonomyEntry = FORM_SECTION_TAXONOMY[section.id as FormSectionTaxonomyId];

  // A section containing a REQUIRED field must never be collapsible — see
  // resolveSectionKind. A collapsed accordion hides the required field, so the
  // user never learns it's required and submit silently fails zod validation.
  const kind = resolveSectionKind(section, fieldsByName);

  // Default hasValue: any listed field has a non-empty value in form state.
  const hasValue =
    section.hasValue ??
    ((form) => {
      const values = form.getValues();
      return section.fieldNames.some((name) => {
        const v = (values)[name];
        if (v === undefined || v === null || v === "") return false;
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === "object") return Object.keys(v).length > 0;
        if (typeof v === "boolean") return v;
        return true;
      });
    });

  return {
    id: section.id,
    kind,
    title: section.title ?? taxonomyEntry?.title ?? section.id,
    summary: section.summary ?? taxonomyEntry?.summary,
    icon: taxonomyEntry?.icon,
    defaultOpen: section.defaultOpen,
    // Only meaningful when the section stayed `collapsible` (resolveSectionKind
    // forces required-field sections to `always`, which ignores this flag).
    collapseOnMobileOnly: kind === "collapsible" ? section.collapseOnMobileOnly : undefined,
    hasValue,
    render: () => (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
        {section.fieldNames.map((name) => {
          const field = fieldsByName.get(name);
          if (!field) return null;
          return (
            <FieldByName
              key={name}
              fieldName={name}
              field={field}
              createFieldContext={createFieldContext}
            />
          );
        })}
      </div>
    ),
  };
}

/**
 * Renders the form content including fields, template selector, and actions.
 *
 * When `sections` + `form` are supplied (from `SimpleTableConfig.createFormSections`),
 * fields render through `<FormSectionGroup>` for progressive disclosure.
 * Otherwise renders the historical flat 2-col grid — backward compatible.
 */
export const FormContent = React.memo(function FormContent({
  entityName,
  editableFields,
  createFieldContext,
  TemplateSelector,
  handleTemplateSelect,
  selectedTemplate,
  isCreating,
  isLoading,
  isSubmitting,
  hasValidationErrors,
  submissionError,
  displayName,
  onSubmit,
  onSaveTemplate,
  onCancel,
  onFormSubmit,
  sections,
  form,
}: FormContentProps) {
  const fieldsByName = useMemo(() => new Map(editableFields), [editableFields]);

  // Entity key (e.g. "deals") for field-help "?" popovers. The render context
  // carries it on `config.entity`; derive once and provide to all FieldWrappers.
  const helpEntity = useMemo(() => {
    const sampleField = editableFields[0]?.[0];
    if (!sampleField) return undefined;
    const ctxConfig = createFieldContext(sampleField).config as { entity?: string } | undefined;
    return ctxConfig?.entity;
  }, [editableFields, createFieldContext]);

  const useSections = Boolean(sections && sections.length > 0 && form);

  // Build descriptors for the sectioned path.
  const descriptors = useMemo(() => {
    if (!useSections || !sections) return [];
    return sections.map((s) => toDescriptor(s, fieldsByName, createFieldContext));
  }, [useSections, sections, fieldsByName, createFieldContext]);

  // Identify any editable fields NOT assigned to a section, so we don't drop
  // them silently when an entity's `createFormSections` is incomplete.
  const orphanFields = useMemo(() => {
    if (!useSections || !sections) return [];
    const assigned = new Set<string>();
    sections.forEach((s) => s.fieldNames.forEach((n) => assigned.add(n)));
    return editableFields.filter(([name]) => !assigned.has(name));
  }, [useSections, sections, editableFields]);

  return (
    <FormFieldEntityProvider entity={helpEntity}>
    <form
      id={entityName ? `create-${entityName}-form` : undefined}
      data-entity={entityName}
      onSubmit={onFormSubmit}
      className="space-y-4 sm:space-y-5"
    >
      {/* Template Selector */}
      {TemplateSelector && (
        <TemplateSelector
          onTemplateSelect={handleTemplateSelect}
          selectedTemplate={selectedTemplate}
          className="mb-6"
        />
      )}

      {useSections && form ? (
        <>
          <FormSectionGroup form={form} sections={descriptors} />
          {orphanFields.length > 0 ? (
            <div className="rounded-md border border-dashed border-border bg-card p-4">
              <p className="mb-3 text-xs text-muted-foreground">
                Additional fields
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                {orphanFields.map(([fieldName, field]) => (
                  <FieldByName
                    key={fieldName}
                    fieldName={fieldName}
                    field={field}
                    createFieldContext={createFieldContext}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </>
      ) : (
        /* Fallback: historical flat 2-col grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
          {editableFields.map(([fieldName, field]) => (
            <FieldByName
              key={fieldName}
              fieldName={fieldName}
              field={field}
              createFieldContext={createFieldContext}
            />
          ))}
        </div>
      )}

      {/* Submit Button and Save Template */}
      <FormActions
        isCreating={isCreating}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        hasValidationErrors={hasValidationErrors}
        submissionError={submissionError}
        displayName={displayName}
        onSubmit={onSubmit}
        onSaveTemplate={onSaveTemplate}
        onCancel={onCancel}
      />
    </form>
    </FormFieldEntityProvider>
  );
});
